// Unused imports kept for future use
// import type { Market as RealMarket, Outcome as RealOutcome } from "@web/features/home/use-markets"
// import { convertWeiPriceToFormats } from "./price-conversion"
import { OUTCOME_COLOR_OVERRIDES } from "./team-colors"
// import { OUTCOME_COLORS } from "./team-colors"
import { TEAM_NAME_OVERRIDES } from "./team-name-mapping"

/**
 * Market Utilities for Color Assignment and Team Name Normalization
 *
 * This module provides a comprehensive system for:
 * 1. Automatic team name normalization (e.g., "white sox" → "chicago white sox")
 * 2. Consistent color assignment across different team name variations
 * 3. Fallback color schemes for teams without specific overrides
 *
 * The system automatically handles:
 * - Short team names → Full team names
 * - Multiple sports with same team names (MLB vs NFL vs CFB)
 * - Case-insensitive matching
 * - Fallback to index-based color assignment
 *
 * Usage Examples:
 * ```typescript
 * // These all return the same colors for Chicago White Sox:
 * getOutcomeColors("white sox", 0)           // → Chicago White Sox colors
 * getOutcomeColors("White Sox", 0)           // → Chicago White Sox colors
 * getOutcomeColors("CHICAGO WHITE SOX", 0)   // → Chicago White Sox colors
 *
 * // Team name normalization:
 * normalizeTeamName("white sox")             // → "chicago white sox"
 * getFullTeamName("yankees")                 // → "new york yankees"
 * hasTeamColorOverride("bears")              // → true (has NFL Bears override)
 * ```
 */

// Market type (unused but kept for future use)
// type MarketType = "matchup" | "multivariate" | "future"

// Display title mapping (unused but kept for future use)
// const getDisplayTitle = (title: string): string => {
//   const titleMap: Record<string, string> = {
//     // Generic patterns
//     "Who will win the 2025 Mens Singles US Open Tennis Tournament?": "2025 US Open Men's Singles",
//     "Who will win the 2025 Womens Singles US Open Tennis Tournament?": "2025 US Open Women's Singles",
//     "Winning Driver of Formula 1 Dutch Grand Prix 2025?": "2025 Dutch Grand Prix",
//     '"Driver of the Day" for Formula 1 Dutch Grand Prix 2025?': "2025 Dutch GP Driver of the Day",
//     "Arsenal at Liverpool - EPL Regular Season - August 31, 2025 (11:30amET)": "Arsenal vs Liverpool",
//   }
//   return titleMap[title] || title
// }

/**
 * Determine the type of market based on its characteristics
 * (unused but kept for future use)
 *
 * Market Types:
 * - Future markets: openedAt timestamp is in the future
 * - Matchup markets: exactly 2 outcomes (e.g., Arsenal vs Liverpool)
 * - Multivariate markets: 3+ outcomes (e.g., Tennis tournament with 6+ players)
 * - Binary markets: 2 outcomes with market group (e.g., NBA East vs West)
 *
 * Note: Binary markets are detected as "matchup" type but can be identified
 * by checking if market.groupId is set
 */
// function getMarketType(market: RealMarket, outcomes: RealOutcome[]): MarketType {
//   // Future markets: openedAt timestamp is in the future
//   const now = Math.floor(Date.now() / 1000)
//   if (market.openedAt && market.openedAt > now) {
//     return "future"
//   }
//
//   // Matchups: exactly 2 outcomes
//   if (outcomes.length === 2) {
//     return "matchup"
//   }
//
//   // Multivariate: more than 2 outcomes
//   if (outcomes.length > 2) {
//     return "multivariate"
//   }
//
//   // Default to multivariate for any other case
//   return "multivariate"
// }

/**
 * Check if a market is a binary market (2 outcomes with a market group)
 * (unused but kept for future use)
 * @param market - The market to check
 * @param outcomes - The market outcomes
 * @returns true if it's a binary market, false otherwise
 *
 * Binary markets are a special case of matchup markets that have:
 * - Exactly 2 outcomes
 * - A market group (groupId is set)
 * - Examples: NBA East vs West, Conference championships
 */
// function isBinaryMarket(market: RealMarket, outcomes: RealOutcome[]): boolean {
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   return outcomes.length === 2 && !!(market ).groupId
// }

/**
 * Get colors for an outcome, with manual overrides taking precedence
 * (unused but kept for future use)
 *
 * This function automatically handles team name normalization:
 * - "white sox" → "chicago white sox" → gets Chicago White Sox colors
 * - "yankees" → "new york yankees" → gets Yankees colors
 * - "bears" → "chicago bears" → gets Bears colors (NFL)
 *
 * @param outcomeName - The outcome/team name
 * @param colorIndex - The index for fallback color assignment
 * @returns Object with primary and secondary colors
 */
// function getOutcomeColors(outcomeName: string, colorIndex: number): { primary: string; secondary: string } {
//   // First, check if we need to override the team name for color lookup
//   const normalizedName = outcomeName.toLowerCase().trim()
//   const overrideName = TEAM_NAME_OVERRIDES[normalizedName]
//
//   // Use the override name if it exists, otherwise use the original name
//   const nameToLookup = overrideName || outcomeName
//
//   // Check for manual override first (case-insensitive)
//   const overrideKey = Object.keys(OUTCOME_COLOR_OVERRIDES).find(
//     (key) => key.toLowerCase() === nameToLookup.toLowerCase(),
//   )
//
//   if (overrideKey) {
//     const colors = OUTCOME_COLOR_OVERRIDES[overrideKey]
//     if (colors) {
//       return colors
//     }
//   }
//
//   // For matchup markets (2 outcomes), ensure consistent color assignment
//   // Left team (index 1/awayTeam) gets green, right team (index 0/homeTeam) gets blue
//   if (colorIndex === 0) {
//     return { primary: "#006BB6", secondary: "#F58426" } // Blue with orange (right side)
//   } else if (colorIndex === 1) {
//     return { primary: "#007A33", secondary: "#FFFFFF" } // Green with white (left side)
//   }
//
//   // Fall back to default color palette for other cases
//   const selectedColors = OUTCOME_COLORS[colorIndex % OUTCOME_COLORS.length]
//   if (selectedColors) {
//     return selectedColors
//   }
//
//   // Final fallback
//   return { primary: "#006BB6", secondary: "#F58426" }
// }

// Convert real market to component format (unused but kept for future use)
// function convertRealMarketToComponentFormat(realMarket: RealMarket, realOutcomes: RealOutcome[]) {
//   const marketType = getMarketType(realMarket, realOutcomes)
//
//   // Convert outcomes with proper price formatting
//   const outcomes = realOutcomes.map((outcome, index) => {
//     const { percentage } = convertWeiPriceToFormats(outcome.price)
//
//     const selectedColors = getOutcomeColors(outcome.name, index)
//
//     return {
//       slug: outcome.slug,
//       name: outcome.name,
//       // eslint-disable-next-line @typescript-eslint/no-explicit-any
//       fanbase: (outcome ).fanbase,
//       acronym: outcome.acronym,
//       price: percentage, // Use percentage format (e.g., "16.67")
//       seed: (index + 1).toString(), // Generate seed based on index
//       // Use team image mapping for real data
//       imageSrc: "",
//       primaryColor: selectedColors.primary,
//       secondaryColor: selectedColors.secondary,
//       state: outcome.state as "active" | "eliminated" | "won",
//       marketSlug: outcome.marketSlug,
//     }
//   })
//
//   // Determine market state based on timestamps
//   const now = Math.floor(Date.now() / 1000)
//   let state: "live" | "upcoming" | "final" = "live"
//
//   if (realMarket.resolvedAt) {
//     state = "final"
//   } else if (realMarket.openedAt && realMarket.openedAt > now) {
//     state = "upcoming"
//   } else if (realMarket.closedAt <= now) {
//     state = "live" // This should probably be "closed" but keeping as "live" for now
//   }
//
//   // Map category to tags
//   const categoryToTags: Record<string, string[]> = {
//     basketball: ["basketball"],
//     tennis: ["tennis"],
//     golf: ["golf"],
//     "european-football": ["european-football"],
//     "american-football": ["american-football"],
//     baseball: ["baseball"],
//     f1: ["f1"],
//     other: ["other"],
//   }
//
//   const tags = categoryToTags[realMarket.category] || [realMarket.category]
//
//   return {
//     name: realMarket.name,
//     slug: realMarket.slug,
//     outcomes,
//     imageUrl: "",
//     volume: 0, // Not available in real data
//     progress: calculateProgress(realMarket.openedAt ?? 0, realMarket.closedAt, realMarket.resolvedAt),
//     ends: new Date(realMarket.closedAt * 1000),
//     dateStart: new Date((realMarket.openedAt ?? 0) * 1000),
//     dateEnd: new Date(realMarket.closedAt * 1000),
//     heldOutcomes: [], // Not available in real data
//     isFinal: !!realMarket.resolvedAt,
//     state,
//     tags,
//     category: realMarket.category,
//     sport: realMarket.category,
//   }
// }

/**
 * Calculate progress percentage based on market timestamps
 * (unused but kept for future use)
 */
// function calculateProgress(openedAt: number, closedAt: number, resolvedAt?: number): number {
//   const now = Math.floor(Date.now() / 1000)
//
//   if (resolvedAt) {
//     return 100 // Market is resolved
//   }
//
//   if (now < openedAt) {
//     return 0 // Market hasn't opened yet
//   }
//
//   if (now >= closedAt) {
//     return 100 // Market is closed
//   }
//
//   const totalDuration = closedAt - openedAt
//   const elapsed = now - openedAt
//   const progress = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100)
//
//   return Math.round(progress)
// }

/**
 * Convert real outcomes to matchup format for MarketMatchupCard
 * (unused but kept for future use)
 */
// function convertOutcomesToMatchup(outcomes: RealOutcome[]): {
//   homeTeam: {
//     name: string
//     shortName: string
//     logo: string
//     score: number
//     winPercentage: number
//     trend: "up" | "down" | "neutral"
//     color: string
//     secondaryColor: string
//   }
//   awayTeam: {
//     name: string
//     shortName: string
//     logo: string
//     score: number
//     winPercentage: number
//     trend: "up" | "down" | "neutral"
//     color: string
//     secondaryColor: string
//   }
// } | null {
//   if (outcomes.length !== 2) {
//     return null
//   }
//
//   const [outcome1, outcome2] = outcomes
//   if (!outcome1 || !outcome2) return null
//
//   // Parse win percentages from prices (prices are already in percentage format from convertRealMarketToComponentFormat)
//   const percentage1 = parseFloat(outcome1.price) || 50
//   const percentage2 = parseFloat(outcome2.price) || 50
//
//   // Generate colors based on team names, with manual overrides taking precedence
//   const colors1 = getOutcomeColors(outcome1.name, 0)
//   const colors2 = getOutcomeColors(outcome2.name, 1)
//
//   return {
//     homeTeam: {
//       name: outcome1.name,
//       shortName: outcome1.acronym || outcome1.name.split(" ").pop() || outcome1.name,
//       logo: "", // Not available in real data
//       score: 0, // Not available in real data
//       winPercentage: percentage1,
//       trend: "neutral" as const,
//       color: colors1.primary,
//       secondaryColor: colors1.secondary,
//     },
//     awayTeam: {
//       name: outcome2.name,
//       shortName: outcome2.acronym || outcome2.name.split(" ").pop() || outcome2.name,
//       logo: "", // Not available in real data
//       score: 0, // Not available in real data
//       winPercentage: percentage2,
//       trend: "neutral" as const,
//       color: colors2.primary,
//       secondaryColor: colors2.secondary,
//     },
//   }
// }

/**
 * Normalize team names for consistent color assignment
 * (unused but kept for future use)
 * @param teamName - The team name to normalize
 * @returns The normalized team name for color lookup
 */
// function normalizeTeamName(teamName: string): string {
//   const normalizedName = teamName.toLowerCase().trim()
//   return TEAM_NAME_OVERRIDES[normalizedName] || teamName
// }

/**
 * Get the full team name for a given team name, considering the league/category
 * @param teamName - The team name (can be short or full)
 * @param category - The league/category (e.g., "nfl", "mlb", "cfb")
 * @returns The full team name if found, otherwise the original name
 */
export function getFullTeamName(teamName: string, category?: string): string {
  const normalizedName = teamName.toLowerCase().trim()
  const normalizedCategory = category?.toLowerCase().trim()

  // First check if we have a league-specific team name override
  if (normalizedCategory && TEAM_NAME_OVERRIDES[`${normalizedCategory} ${normalizedName}`]) {
    const overrideName = TEAM_NAME_OVERRIDES[`${normalizedCategory} ${normalizedName}`]
    if (overrideName) {
      return overrideName
    }
  }

  // Then check for general team name override (e.g., "eagles" -> "philadelphia eagles")
  if (TEAM_NAME_OVERRIDES[normalizedName]) {
    const overrideName = TEAM_NAME_OVERRIDES[normalizedName]
    if (overrideName) {
      return overrideName
    }
  }

  // Check if the name already exists in our color overrides (it's already a recognized full name)
  if (OUTCOME_COLOR_OVERRIDES[normalizedName]) {
    return teamName // Return as-is since it's already a recognized full name
  }

  // For names that might be partial, try to find a match in our color overrides
  // Prioritize matches that align with the category if provided
  let matchingKey: string | undefined

  if (normalizedCategory) {
    // First try to find category-specific matches
    matchingKey = Object.keys(OUTCOME_COLOR_OVERRIDES).find((key) => {
      const keyLower = key.toLowerCase()
      // Check if the key contains the category and the team name
      return (
        (keyLower.includes(normalizedCategory) || keyLower.includes(normalizedCategory.replace("-", " "))) &&
        (keyLower.includes(normalizedName) || normalizedName.includes(keyLower))
      )
    })

    if (matchingKey) {
      return matchingKey
    }
  }

  // Fall back to general partial matching
  matchingKey = Object.keys(OUTCOME_COLOR_OVERRIDES).find(
    (key) => key.toLowerCase().includes(normalizedName) || normalizedName.includes(key.toLowerCase()),
  )

  if (matchingKey) {
    return matchingKey
  }

  // If no match found, return the original name
  return teamName
}

/**
 * Check if a team name has a color override
 * (unused but kept for future use)
 * @param teamName - The team name to check
 * @returns true if the team has a color override, false otherwise
 */
// function hasTeamColorOverride(teamName: string): boolean {
//   const normalizedName = teamName.toLowerCase().trim()
//   return normalizedName in OUTCOME_COLOR_OVERRIDES || normalizedName in TEAM_NAME_OVERRIDES
// }

/**
 * Generate a consistent color based on a string
 * (unused but kept for future use)
 */
// function generateColor(str: string): string {
//   let hash = 0
//   for (let i = 0; i < str.length; i++) {
//     hash = str.charCodeAt(i) + ((hash << 5) - hash)
//   }
//
//   const hue = hash % 360
//   const saturation = 70 + (hash % 20) // 70-90%
//   const lightness = 40 + (hash % 20) // 40-60%
//
//   return `hsl(${hue}, ${saturation}%, ${lightness}%)`
// }
